import React from 'react';

import { Typography, Button } from '@material-ui/core';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import action_library_messages from 'messages/action_library_messages';
import PATHS from 'router/PATHS';

import useStyles from './ErrorState.styles';

type Props = {
  error?: Error | AxiosError;
  onRetry: () => void;
};

type ErrorType = 'unauthorized' | 'timeout' | 'general';

const ErrorState: React.FC<Props> = ({ error, onRetry }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const styles = useStyles();

  // Determine error type
  const getErrorType = (): ErrorType => {
    if (!error) return 'general';

    // Check for AxiosError with response
    if ('response' in error && error.response) {
      const { status } = error.response;
      if (status === 403) {
        return 'unauthorized';
      }
    }

    // Check for timeout (ECONNABORTED or timeout in message)
    if ('code' in error && error.code === 'ECONNABORTED') {
      return 'timeout';
    }
    if (error.message && (error.message.includes('timeout') || error.message.includes('Timeout'))) {
      return 'timeout';
    }

    return 'general';
  };

  const errorType = getErrorType();

  const handleBackToStart = () => {
    history.push(PATHS.ROOT);
  };

  const getTitle = () => {
    switch (errorType) {
      case 'unauthorized':
        return t(action_library_messages.error_state.unauthorized.title);
      case 'timeout':
        return t(action_library_messages.error_state.timeout.title);
      default:
        return t(action_library_messages.error_state.title);
    }
  };

  const getDescription = () => {
    switch (errorType) {
      case 'unauthorized':
        return t(action_library_messages.error_state.unauthorized.description);
      case 'timeout':
        return t(action_library_messages.error_state.timeout.description);
      default:
        return t(action_library_messages.error_state.description);
    }
  };

  const getButtonText = () => {
    switch (errorType) {
      case 'unauthorized':
        return t(action_library_messages.error_state.unauthorized.back_to_start);
      case 'timeout':
        return t(action_library_messages.error_state.timeout.retry);
      default:
        return t(action_library_messages.error_state.retry);
    }
  };

  const handleButtonClick = () => {
    if (errorType === 'unauthorized') {
      handleBackToStart();
    } else {
      onRetry();
    }
  };

  return (
    <div className={styles.root}>
      <Typography className={styles.title} variant='h5'>
        {getTitle()}
      </Typography>
      <Typography className={styles.description} variant='body1'>
        {getDescription()}
      </Typography>
      <Button className={styles.button} color='primary' onClick={handleButtonClick} variant='contained'>
        {getButtonText()}
      </Button>
    </div>
  );
};

export default ErrorState;
